import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useGlobals from 'redux/globals';
import {
  authPaquerypointSelector,
  authTokenSelector,
  authProfile,
} from 'redux/auth/selectors';
import { logout } from 'redux/logout';
import { NavigationLayout } from '@paquery-team/lib-app-layout';
import {
  ProfileOutlined,
  ImportOutlined,
  ContainerOutlined,
  SearchOutlined,
  ExportOutlined,
  UserOutlined,
  UserSwitchOutlined,
  GiftOutlined,
  QuestionCircleOutlined,
  TruckOutlined,
  MessageOutlined,
  InboxOutlined,
  ShopOutlined,
  BankOutlined,
  RadarChartOutlined,
  DollarOutlined,
  EnvironmentOutlined,
  ContactsOutlined,
} from '@ant-design/icons';
import { Switch, Route, Redirect, useLocation } from 'react-router-dom';
import SITELINKS from 'constants/sitelinks';
import {
  ROLE,
  PaqueryPointRoles,
  LogisticOperatorRoles,
} from 'constants/roles';
import ArrivePacket from 'routes/app/arrivePacket';
import DeliverPickup from 'routes/app/deliverPickup';
import PackagesList from 'routes/app/packageList';
import PackagesHistory from 'routes/app/packageHistory';
import PackageView from 'routes/app/packageView';
import PackagesWithPaquersHistorical from 'routes/app/packageWithPaquersHistorical';
import PackageModify from 'routes/app/packageModify';
import { ConfigProvider } from 'antd';
import esES from 'antd/locale/es_ES';
import { useAlert } from 'services/AlertService';
import { useQueryClient } from '@tanstack/react-query';
import NavigationTracker from 'util/NavigationTracker';
import RoleRoute from 'routes/RoleRoute';
import filterMenuByRole from 'util/menuRoles';
import { collectionsEnabledFor } from 'constants/flags';
import PackagesWithPaquers from './packagesWithPaquers';
import PackagesWithoutZone from './packagesWithoutZone';
import UserProfile from './userProfile';
import PackageSearchByCode from './packageSearchByCode';
import DeparturePaqueryPointPacket from './departurePaqueryPoint';
import DepartureLogisticOperatorPacket from './departureLogisticOperator';
import DeparturePaqueryPointHistory from './departurePaqueryPointHistory';
import PaquerList from './paquerList';
import Paquer from './paquerModify';
import PaquerAdd from './paquerAdd';
import Support from './support';
import JourneysList from './journeys';
import CollectionsV2, { CollectionsHistory } from './collections';
import Journey from './journeys/dashboard';

import {
  refreshFCMToken,
  useUnreadsNotifications,
} from 'services/NotificationService';
import NotificationInbox from './notificationInbox';
import Messaging from './messaging';
import CollectionList from './collectionList';
import CollectionPoints from './collectionPoints';
import CollectionIngreso from './collectionIngreso';
import { useFirebaseNotifications } from 'hooks/useFirebaseNotifications';
import { GOOGLE_MAPS_API_KEY } from 'constants/api';
import { useJsApiLoader } from '@react-google-maps/api';

// --- Módulos portados desde webapp-opl ---
import UserList from './userList';
import UserAdd from './userAdd';
import UserEdit from './userEdit';
import PaqueryPointList from './paquerypointList';
import PaqueryPointAdd from './paquerypointAdd';
import PaqueryPointModify from './paquerypointModify';
import MarketplaceList from './marketplaceList';
import MarketplaceAdd from './marketplaceAdd';
import MarketplaceModify from './marketplaceModify';
import ZonesView from './zone/list';
import ZoneAdd from './zone/add';
import ZoneEdit from './zone/edit';
import GroupingZoneAdd from './zone/groupAdd';
import GroupingZoneEdit from './zone/groupEdit';
import RatesList from './rateList';
import AssignedDrivers from './assignedDrivers';
import AssignedRoutes from './assignedRoutes';
import PackageMassiveList from './packageMassiveList';
import PackageSend from './packageSend';

const ALL = [...PaqueryPointRoles, ...LogisticOperatorRoles];
const PP = PaqueryPointRoles;
const OPL = LogisticOperatorRoles;
const OPL_ADMIN = [ROLE.OPL_ADMIN];

// ponytail: NavigationLayout siempre renderiza <img src={logo}> en el header, y
// con src vacío el browser muestra el alt. Un GIF transparente de 1px lo deja
// invisible sin romper el `space-between` que empuja el logout a la derecha.
// Si algún día va el logo de Chazki, reemplazar esta constante por el import.
const NO_LOGO =
  'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

const App = () => {
  useGlobals();
  const dispatch = useDispatch();
  const sessionToken = useSelector(authTokenSelector);
  const paquerypoint = useSelector(authPaquerypointSelector);
  const profile = useSelector(authProfile);
  const collectionsEnabled = collectionsEnabledFor(profile?.logisticOperatorID);
  const { data: alert } = useAlert();
  const queryClient = useQueryClient();
  const { data: notifications } = useUnreadsNotifications();
  const username =
    paquerypoint?.detail ?? `${profile?.name ?? ''} ${profile?.lastName ?? ''}`;

  useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: ['drawing', 'places'],
  });
  useFirebaseNotifications();
  useEffect(() => {
    const handleRefreshFCMToken = async () =>
      await refreshFCMToken(sessionToken);

    if (sessionToken) {
      handleRefreshFCMToken();
    }
  }, [sessionToken]);

  const messagingTitle = useMemo(() => {
    if (notifications && notifications.data.length > 0) {
      return `Mensajería (${notifications.data.length})`;
    }
    return 'Mensajería';
  }, [notifications]);

  const inboxSubtitle = useMemo(() => {
    if (notifications && notifications.data.length > 0) {
      return `Entrantes (${notifications.data.length})`;
    }
    return 'Entrantes';
  }, [notifications]);

  const location = useLocation();
  const onNewJourney = location.pathname.startsWith('/journeys/new');
  const newJourneyKey = (base) => (onNewJourney ? base : `${base}-idle`);

  const menuItems = useMemo(() => {
    const menu = [
      {
        key: 'packagesBySearchCode',
        title: 'Buscar por código',
        icon: <SearchOutlined />,
        link: SITELINKS.packages.bySearchCode,
        roles: ALL,
      },
      {
        key: 'arrivePacket',
        title: 'Ingresar',
        icon: <ImportOutlined />,
        link: SITELINKS.packet.entry,
        roles: ALL,
      },
      {
        key: 'deliverPickup',
        title: 'Entrega Pickup',
        icon: <GiftOutlined />,
        link: SITELINKS.packet.deliver,
        roles: PP,
      },
      {
        key: 'journeys',
        title: 'Rutas',
        icon: <TruckOutlined />,
        roles: PP,
        subMenuItems: [
          {
            key: 'journeysTracking',
            title: 'Seguimiento',
            link: SITELINKS.journeysTracking,
          },
          {
            key: 'journeysPrep',
            title: 'Preparación',
            link: SITELINKS.journeysPrep,
          },
          {
            key: 'journeysHistory',
            title: 'Historial',
            link: SITELINKS.journeysHistory,
          },
          ...(collectionsEnabled
            ? [
                {
                  key: 'journeysTemplates',
                  title: 'Plantillas',
                  link: SITELINKS.journeysTemplates,
                },
              ]
            : []),
          {
            key: 'journeysCreate',
            type: 'group',
            title: 'Crear ruta',
            items: [
              {
                key: newJourneyKey('journeysNewShipments'),
                title: 'De envíos',
                link: SITELINKS.journeysNewShipments,
              },
              ...(collectionsEnabled
                ? [
                    {
                      key: newJourneyKey('journeysNewCollections'),
                      title: 'De colectas',
                      link: SITELINKS.journeysNewCollections,
                    },
                  ]
                : []),
            ],
          },
        ],
      },
      {
        key: 'assignee',
        title: 'Asignados',
        icon: <TruckOutlined />,
        roles: OPL,
        subMenuItems: [
          {
            key: 'assignedDrivers',
            title: 'Drivers',
            icon: <ContactsOutlined />,
            link: SITELINKS.assignee.drivers,
          },
          {
            key: 'assignedRoutes',
            title: 'Rutas',
            icon: <EnvironmentOutlined />,
            link: SITELINKS.assignee.routes,
          },
        ],
      },
      {
        key: 'departurePacket',
        title: 'Transferir',
        icon: <ExportOutlined />,
        roles: ALL,
        subMenuItems: [
          {
            key: 'crossdockingDeparturePacket',
            title: 'Transferir',
            link: SITELINKS.packet.departure.paqueryPoint,
            roles: PP,
          },
          {
            key: 'logisticOperatorDeparturePacket',
            title: 'Transferir',
            link: SITELINKS.packet.departure.logisticOperator,
            roles: OPL,
          },
          {
            key: 'historicDeparture',
            title: 'Histórico',
            link: SITELINKS.packet.departure.history,
          },
        ],
      },
      {
        key: 'packages',
        title: 'Paquetes',
        icon: <ContainerOutlined />,
        roles: ALL,
        subMenuItems: [
          {
            key: 'packageList',
            title: 'En transito',
            link: SITELINKS.packages.list,
            roles: ALL,
          },
          {
            key: 'packageHistory',
            title: 'Histórico',
            link: SITELINKS.packages.history,
            roles: ALL,
          },
          {
            key: 'packagePaquer',
            title: 'Paquetes por paquer',
            link: SITELINKS.packages.withPaquers,
            roles: ALL,
          },
          {
            key: 'packagePaquerHistory',
            title: 'Histórico por paquer',
            link: SITELINKS.packages.withPaquersHistorical,
            roles: ALL,
          },
          {
            key: 'packageWithoutZone',
            title: 'Sin zona',
            link: SITELINKS.packages.withoutZone,
            roles: ALL,
          },
          {
            key: 'massivePackageList',
            title: 'Alta masiva',
            link: SITELINKS.massive.list,
            roles: OPL,
          },
          {
            key: 'packageSend',
            title: 'Alta individual',
            link: SITELINKS.packages.send,
            roles: OPL,
          },
        ],
      },
      {
        key: 'paquers',
        title: 'Paquers',
        icon: <UserOutlined />,
        link: SITELINKS.paquers.list,
        roles: PP,
      },
      {
        key: 'collections',
        title: 'Colectas',
        icon: <InboxOutlined />,
        roles: ALL,
        subMenuItems: [
          ...(collectionsEnabled
            ? [
                {
                  key: 'collectionPoints',
                  title: 'Puntos de colecta',
                  link: SITELINKS.collections.points,
                  roles: ALL,
                },
                {
                  key: 'collectionsTransit',
                  title: 'En tránsito',
                  link: SITELINKS.collections.transit,
                  roles: PP,
                },
                {
                  key: 'collectionsHistory',
                  title: 'Histórico',
                  link: SITELINKS.collections.history,
                  roles: PP,
                },
              ]
            : []),
          {
            key: 'collectionRequests',
            title: collectionsEnabled
              ? 'Solicitudes (versión anterior)'
              : 'Solicitudes',
            link: SITELINKS.collections.requests,
            roles: PP,
          },
          {
            key: 'collectionIntake',
            title: 'Ingreso',
            link: SITELINKS.collections.intake,
            roles: PP,
          },
        ],
      },
      {
        key: 'administration',
        title: 'Administración',
        icon: <UserOutlined />,
        roles: OPL,
        subMenuItems: [
          {
            key: 'users',
            title: 'Usuarios',
            icon: <UserOutlined />,
            link: SITELINKS.users.list,
            roles: OPL,
          },
          {
            key: 'paquerList',
            title: 'Paquers',
            icon: <UserSwitchOutlined />,
            link: SITELINKS.paquers.list,
            roles: OPL,
          },
          {
            key: 'paquerypoint',
            title: 'PaQuery Points',
            icon: <BankOutlined />,
            link: SITELINKS.paquerypoint.list,
            roles: OPL,
          },
          {
            key: 'marketplace',
            title: 'Marketplaces',
            icon: <ShopOutlined />,
            link: SITELINKS.marketplace.list,
            roles: OPL,
          },
          {
            key: 'zones',
            title: 'Zonas',
            icon: <RadarChartOutlined />,
            link: SITELINKS.zones.list,
            roles: OPL_ADMIN,
          },
          {
            key: 'rates',
            title: 'Tarifas',
            icon: <DollarOutlined />,
            link: SITELINKS.rates.list,
            roles: OPL_ADMIN,
          },
        ],
      },
      {
        key: 'profile',
        title: 'Perfil',
        icon: <ProfileOutlined />,
        link: SITELINKS.profile,
        roles: ALL,
      },
      {
        key: 'messaging',
        title: messagingTitle,
        icon: <MessageOutlined />,
        roles: PP,
        subMenuItems: [
          { key: 'inbox', title: inboxSubtitle, link: SITELINKS.inbox },
          {
            key: 'outbound',
            title: 'Salientes',
            link: SITELINKS.messaging.outbound,
          },
        ],
      },
      {
        key: 'support',
        title: 'Soporte',
        icon: <QuestionCircleOutlined />,
        link: SITELINKS.support.index,
        roles: ALL,
      },
    ];
    return filterMenuByRole(menu, profile?.userRoleID);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    messagingTitle,
    inboxSubtitle,
    profile?.userRoleID,
    collectionsEnabled,
    onNewJourney,
  ]);

  return (
    <ConfigProvider
      locale={esES}
      theme={{ token: { colorBgLayout: '#257de2', colorPrimary: '#3cd42e' } }}
    >
      <NavigationTracker />
      <NavigationLayout
        menuItems={menuItems ?? []}
        userName={username}
        logo={NO_LOGO}
        alert={{
          message: alert?.message,
          type: alert?.type ?? 'info',
          closable: true,
        }}
        logout={() => {
          queryClient.invalidateQueries(['alert']);
          dispatch(logout());
        }}
        showLangSelector={false}
      >
        <Switch>
          <Route
            path={SITELINKS.packages.bySearchCode}
            render={(props) => <PackageSearchByCode {...props} />}
          />
          <Route
            path={SITELINKS.packet.entry}
            render={(props) => <ArrivePacket {...props} />}
          />
          <RoleRoute
            roles={PP}
            path={SITELINKS.packet.deliver}
            render={(props) => <DeliverPickup {...props} />}
          />
          <Route
            path={SITELINKS.packet.departure.history}
            render={(props) => <DeparturePaqueryPointHistory {...props} />}
          />
          <RoleRoute
            roles={PP}
            path={SITELINKS.journeys}
            render={(props) => <JourneysList {...props} />}
          />
          <RoleRoute
            roles={PP}
            path={`${SITELINKS.journey}/:id`}
            render={(props) => <Journey {...props} />}
          />
          <RoleRoute
            roles={PP}
            path={SITELINKS.packet.departure.paqueryPoint}
            render={(props) => <DeparturePaqueryPointPacket {...props} />}
          />
          <RoleRoute
            roles={OPL}
            path={SITELINKS.packet.departure.logisticOperator}
            render={(props) => <DepartureLogisticOperatorPacket {...props} />}
          />
          <Route
            path={SITELINKS.packages.history}
            render={(props) => <PackagesHistory {...props} />}
            exact
          />
          <RoleRoute
            roles={OPL}
            path={SITELINKS.massive.list}
            render={(props) => <PackageMassiveList {...props} />}
          />
          <Route
            path={SITELINKS.packages.send}
            render={(props) =>
              OPL.includes(profile?.userRoleID) || props.location.state?.id ? (
                <PackageSend {...props} />
              ) : (
                <Redirect to={SITELINKS.loggedHome} />
              )
            }
          />
          <Route
            path={SITELINKS.packages.list}
            render={(props) => <PackagesList {...props} />}
            exact
          />
          <Route
            path={SITELINKS.packages.withoutZone}
            render={(props) => <PackagesWithoutZone {...props} />}
            exact
          />
          <Route
            path={SITELINKS.packages.withPaquers}
            render={(props) => <PackagesWithPaquers {...props} />}
            exact
          />
          <Route
            path={SITELINKS.packages.withPaquersHistorical}
            render={(props) => <PackagesWithPaquersHistorical {...props} />}
            exact
          />
          <Route
            path={SITELINKS.packages.view}
            render={(props) => <PackageView {...props} />}
          />
          <Route
            path={SITELINKS.packages.byId}
            render={(props) => <PackageModify {...props} />}
          />
          <Route
            path={SITELINKS.packages.byExternalCode}
            render={(props) => <PackageModify {...props} />}
            exact
          />
          <RoleRoute
            roles={OPL}
            path={SITELINKS.paquers.add}
            render={(props) => <PaquerAdd {...props} />}
          />
          <Route
            path={SITELINKS.paquers.list}
            render={(props) => <PaquerList {...props} />}
            exact
          />
          {collectionsEnabled && (
            <RoleRoute
              roles={ALL}
              path={SITELINKS.collections.points}
              render={(props) => <CollectionPoints {...props} />}
              exact
            />
          )}
          {collectionsEnabled && (
            <RoleRoute
              roles={PP}
              path={SITELINKS.collections.transit}
              render={(props) => <CollectionsV2 {...props} />}
              exact
            />
          )}
          {collectionsEnabled && (
            <RoleRoute
              roles={PP}
              path={SITELINKS.collections.history}
              render={(props) => <CollectionsHistory {...props} />}
              exact
            />
          )}
          <RoleRoute
            roles={PP}
            path={SITELINKS.collections.requests}
            render={(props) => <CollectionList {...props} />}
            exact
          />
          <RoleRoute
            roles={PP}
            path={SITELINKS.collections.intake}
            render={(props) => <CollectionIngreso {...props} />}
            exact
          />
          <Route
            path={SITELINKS.paquers.view}
            render={(props) => <Paquer {...props} />}
          />
          {/* --- Administración (OPL) --- */}
          <RoleRoute
            roles={OPL}
            path={SITELINKS.users.add}
            render={(props) => <UserAdd {...props} />}
          />
          <RoleRoute
            roles={OPL}
            path={SITELINKS.users.edit}
            render={(props) => <UserEdit {...props} />}
          />
          <RoleRoute
            roles={OPL}
            path={SITELINKS.users.list}
            render={(props) => <UserList {...props} />}
          />
          <RoleRoute
            roles={OPL}
            path={SITELINKS.paquerypoint.add}
            render={(props) => <PaqueryPointAdd {...props} />}
          />
          <RoleRoute
            roles={OPL}
            path={SITELINKS.paquerypoint.edit}
            render={(props) => <PaqueryPointModify {...props} />}
          />
          <RoleRoute
            roles={OPL}
            path={SITELINKS.paquerypoint.list}
            render={(props) => <PaqueryPointList {...props} />}
          />
          <RoleRoute
            roles={OPL}
            path={SITELINKS.marketplace.add}
            render={(props) => <MarketplaceAdd {...props} />}
          />
          <RoleRoute
            roles={OPL}
            path={SITELINKS.store.edit}
            render={(props) => <MarketplaceModify {...props} />}
          />
          <RoleRoute
            roles={OPL}
            path={SITELINKS.marketplace.edit}
            render={(props) => <MarketplaceModify {...props} />}
          />
          <RoleRoute
            roles={OPL}
            path={SITELINKS.marketplace.list}
            render={(props) => <MarketplaceList {...props} />}
          />
          <RoleRoute
            roles={OPL_ADMIN}
            path={SITELINKS.zones.add}
            render={(props) => <ZoneAdd {...props} />}
          />
          <RoleRoute
            roles={OPL_ADMIN}
            path={SITELINKS.zones.grouping.add}
            render={(props) => <GroupingZoneAdd {...props} />}
          />
          <RoleRoute
            roles={OPL_ADMIN}
            path={SITELINKS.zones.grouping.route}
            render={(props) => <GroupingZoneEdit {...props} />}
          />
          <RoleRoute
            roles={OPL_ADMIN}
            path={SITELINKS.zones.edit.route}
            render={(props) => <ZoneEdit {...props} />}
          />
          <RoleRoute
            roles={OPL_ADMIN}
            path={SITELINKS.zones.list}
            render={(props) => <ZonesView {...props} />}
          />
          <Redirect exact from="/rates" to={SITELINKS.rates.lastMile} />
          <RoleRoute
            roles={OPL_ADMIN}
            path={SITELINKS.rates.list}
            render={(props) => <RatesList {...props} />}
          />
          <RoleRoute
            roles={OPL}
            path={SITELINKS.assignee.routes}
            render={(props) => <AssignedRoutes {...props} />}
          />
          <RoleRoute
            roles={OPL}
            path={SITELINKS.assignee.drivers}
            render={(props) => <AssignedDrivers {...props} />}
          />
          <Route
            path={SITELINKS.profile}
            render={(props) => <UserProfile {...props} />}
          />
          <RoleRoute
            roles={PP}
            path={SITELINKS.inbox}
            render={() => <NotificationInbox />}
          />
          <RoleRoute
            roles={PP}
            path={SITELINKS.messaging.outbound}
            render={() => <Messaging />}
          />
          <Route path={SITELINKS.support.index} render={() => <Support />} />
          <Redirect to={SITELINKS.packages.bySearchCode} />
        </Switch>
      </NavigationLayout>
    </ConfigProvider>
  );
};

export default App;
